/**
 * Two Column Accordion Carousel Block - Frontend Script
 * 
 * Syncs accordion interactions with carousel slides using SwiperJS
 */

import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { createBlockInit } from '../../src/js/utils/block-init.js';

/**
 * Initialize a single block
 * @param {HTMLElement} accordionElement - The accordion container element
 */
const INIT_ATTR = 'data-accordion-carousel-initialized';

function initBlock(accordionElement) {
	if (accordionElement.hasAttribute(INIT_ATTR)) {
		return;
	}

	accordionElement.setAttribute(INIT_ATTR, 'true');

	const blockId = accordionElement.dataset.carouselSync;
	const carouselElement = blockId
		? document.querySelector(`.btcac-carousel-swiper[data-carousel-id="${blockId}"]`)
		: null;

	let swiper = null;

	if (!carouselElement) {
		initAccordionWithSync(accordionElement, swiper);
		return;
	}

	swiper = new Swiper(carouselElement, {
		modules: [Pagination],
		slidesPerView: 1,
		spaceBetween: 0,
		grabCursor: false,
		allowTouchMove: false,
		pagination: {
			el: carouselElement.querySelector('.swiper-pagination'),
			clickable: true,
			bulletClass: 'btcac-pagination-bullet',
			bulletActiveClass: 'btcac-pagination-bullet--active',
		},
		a11y: {
			enabled: true,
			prevSlideMessage: 'Previous slide',
			nextSlideMessage: 'Next slide',
		},
	});
	
	initAccordionWithSync(accordionElement, swiper);
	syncPaginationToAccordion(carouselElement, accordionElement, swiper);
}

/**
 * Initialize accordion with carousel sync
 * @param {HTMLElement} accordionElement - The accordion container
 * @param {Swiper} swiper - The Swiper instance
 */
function initAccordionWithSync(accordionElement, swiper) {
	const items = Array.from(accordionElement.querySelectorAll('[data-accordion-item]'));
	
	items.forEach((item, index) => {
		const trigger = item.querySelector('[data-accordion-trigger]');
		const content = item.querySelector('[data-accordion-content]');
		
		if (!trigger || !content) return;

		const itemId = `accordion-item-${Date.now()}-${index}`;
		const triggerId = `${itemId}-trigger`;
		const contentId = `${itemId}-content`;
		
		trigger.setAttribute('id', triggerId);
		trigger.setAttribute('aria-controls', contentId);
		content.setAttribute('id', contentId);
		content.setAttribute('role', 'region');
		content.setAttribute('aria-labelledby', triggerId);

		trigger.addEventListener('click', (e) => {
			e.preventDefault();
			
			const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
			
			items.forEach((otherItem) => {
				const otherTrigger = otherItem.querySelector('[data-accordion-trigger]');
				const otherContent = otherItem.querySelector('[data-accordion-content]');
				
				if (otherTrigger && otherContent) {
					otherTrigger.setAttribute('aria-expanded', 'false');
					otherContent.removeAttribute('data-accordion-open');
				}
			});
			
			if (!isExpanded) {
				trigger.setAttribute('aria-expanded', 'true');
				content.setAttribute('data-accordion-open', '');
				
				const slideIndex = parseInt(item.dataset.slideIndex, 10);
				if (!isNaN(slideIndex)) {
					swiper.slideTo(slideIndex);
				}
			}
		});
	});
}

/**
 * Sync pagination clicks back to accordion
 * @param {HTMLElement} carouselElement - The carousel container
 * @param {HTMLElement} accordionElement - The accordion container
 * @param {Swiper} swiper - The Swiper instance
 */
function syncPaginationToAccordion(carouselElement, accordionElement, swiper) {
	const pagination = carouselElement.querySelector('.swiper-pagination');
	
	if (!pagination) return;
	
	swiper.on('slideChange', () => {
		const activeIndex = swiper.activeIndex;
		const accordionItems = accordionElement.querySelectorAll('[data-accordion-item]');
		
		accordionItems.forEach((item, index) => {
			const trigger = item.querySelector('[data-accordion-trigger]');
			const content = item.querySelector('[data-accordion-content]');
			
			if (!trigger || !content) return;
			
			if (index === activeIndex) {
				trigger.setAttribute('aria-expanded', 'true');
				content.setAttribute('data-accordion-open', '');
			} else {
				trigger.setAttribute('aria-expanded', 'false');
				content.removeAttribute('data-accordion-open');
			}
		});
	});
}

const { initAll: initTwoColumnAccordionCarousels } = createBlockInit('[data-carousel-sync]', initBlock);

export { initTwoColumnAccordionCarousels, initBlock };
